import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LocationSelector from '../components/LocationSelector';
import JobCard from '../components/JobCard';
import SwipeableJobCard from '../components/SwipeableJobCard';
import { useAuth } from '../context/AuthContext';
import useIPLocation from '../hooks/useIPLocation';
import { getByName, getFylkeName } from '../data/norwegianCities';
import { isDemoMode } from '../demo/useDemoMode';
import { filterJobs } from '../demo/mockData';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const HomePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const ipLocation = useIPLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFylke, setSelectedFylke] = useState(null);
  const [selectedBy, setSelectedBy] = useState(null);
  const [recommendedJobs, setRecommendedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationSet, setLocationSet] = useState(false);

  // Sett lokasjon automatisk basert på IP eller bruker
  useEffect(() => {
    if (!locationSet) {
      if (isAuthenticated && user?.location_fylke) {
        setSelectedFylke(user.location_fylke);
        if (user.location_city) {
          setSelectedBy(user.location_city);
        }
        setLocationSet(true);
      } else if (!ipLocation.loading && ipLocation.fylkeId) {
        setSelectedFylke(ipLocation.fylkeId);
        if (ipLocation.byId) {
          setSelectedBy(ipLocation.byId);
        }
        setLocationSet(true);
      }
    }
  }, [isAuthenticated, user, ipLocation, locationSet]);

  useEffect(() => {
    fetchRecommendedJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFylke, selectedBy, user]);

  const fetchRecommendedJobs = async () => {
    setLoading(true);
    
    // In demo mode, use mock data
    if (isDemoMode()) {
      const filters = {};
      if (selectedFylke) filters.fylke = selectedFylke;
      if (selectedBy) filters.city = selectedBy;
      
      const mockJobs = filterJobs(filters).slice(0, 12);
      setRecommendedJobs(mockJobs);
      setLoading(false);
      return;
    }

    try {
      const params = new URLSearchParams();
      if (selectedFylke) params.append('fylke', selectedFylke);
      if (selectedBy) params.append('city', selectedBy);
      params.append('limit', '12');
      
      const response = await axios.get(`${API_URL}/jobs/feed/recommended?${params.toString()}`);
      setRecommendedJobs(response.data || []);
    } catch (error) {
      console.error('Feil ved henting av anbefalte annonser:', error);
      setRecommendedJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchTerm.trim()) params.append('q', searchTerm.trim());
    if (selectedFylke) params.append('fylke', selectedFylke);
    if (selectedBy) params.append('city', selectedBy);
    navigate(`/sok?${params.toString()}`);
  };

  const handleFylkeSelect = (fylke) => {
    setSelectedFylke(fylke);
    if (!fylke) setSelectedBy(null);
  };

  const handleBySelect = (by) => {
    setSelectedBy(by);
  };

  const handleJobContact = (job) => {
    if (isAuthenticated) {
      navigate(`/meldinger?job=${job.id}&user=${job.poster?.id}`);
    } else {
      navigate('/logg-inn');
    }
  };

  const handleJobSave = (job) => {
    console.log('Lagre jobb:', job.id);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section with Search */}
      <div className="bg-gradient-to-br from-[#0A1929] to-[#1E3A5F] text-white py-8 md:py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-heading font-bold text-2xl md:text-4xl mb-2 text-center">
            Finn lokale oppdrag nær deg
          </h1>
          <p className="text-white/70 text-sm md:text-base text-center mb-6">
            En enkel vei inn i arbeidslivet for ungdom- lett trygt og digitalt
          </p>
          
          {/* Finn-style Search Bar */}
          <form onSubmit={handleSearch} className="bg-white rounded-xl shadow-2xl p-3 md:p-4">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400">
                  search
                </span>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Søk etter oppdrag, f.eks. 'hagearbeid', 'flyttehjelp'..."
                  className="w-full pl-10 pr-4 py-3 text-[#0A1929] bg-slate-50 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent transition-all"
                />
              </div>
              <button
                type="submit"
                className="bg-gradient-to-r from-[#1E3A5F] to-[#0A1929] text-white px-8 py-3 rounded-lg font-medium hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-lg"
              >
                <span className="material-symbols-outlined">search</span>
                <span>Søk</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Horizontal Fylke Selector */}
      <div className="bg-white border-b border-slate-100 py-4">
        <div className="max-w-7xl mx-auto">
          <LocationSelector
            selected={selectedFylke}
            onSelect={handleFylkeSelect}
            selectedBy={selectedBy}
            onBySelect={handleBySelect}
            horizontal={true}
            showAll={true}
            showLocationButton={true}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-semibold text-lg md:text-xl text-[#0A1929] flex items-center gap-2">
            <span className="material-symbols-outlined text-[#1E3A5F]">
              {selectedFylke ? 'location_on' : 'recommend'}
            </span>
            {selectedBy 
              ? `Oppdrag i ${getByName(selectedBy)}`
              : selectedFylke 
                ? `Oppdrag i ${getFylkeName(selectedFylke)}`
                : isAuthenticated 
                  ? 'Anbefalt for deg'
                  : 'Nyeste oppdrag'
            }
          </h2>
          {recommendedJobs.length > 0 && (
            <button
              onClick={() => navigate('/sok')}
              className="text-sm text-[#1E3A5F] hover:text-[#0A1929] font-medium flex items-center gap-1 transition-colors"
            >
              Se alle
              <span className="material-symbols-outlined text-lg">arrow_forward</span>
            </button>
          )}
        </div>

        {/* Jobs Grid/List */}
        {loading ? (
          <>
            {/* Mobile loading skeleton */}
            <div className="md:hidden space-y-0">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex bg-white border-b border-slate-100 animate-pulse">
                  <div className="w-28 h-28 bg-slate-200"></div>
                  <div className="flex-1 p-3 space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                    <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                    <div className="h-5 bg-slate-200 rounded w-1/4 mt-4"></div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Desktop loading skeleton */}
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-xl shadow-sm overflow-hidden animate-pulse">
                  <div className="aspect-video bg-slate-200"></div>
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-slate-200 rounded w-1/4"></div>
                    <div className="h-5 bg-slate-200 rounded w-3/4"></div>
                    <div className="h-4 bg-slate-200 rounded w-full"></div>
                    <div className="flex justify-between pt-3">
                      <div className="h-4 bg-slate-200 rounded w-1/4"></div>
                      <div className="h-5 bg-slate-200 rounded w-1/4"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : recommendedJobs.length > 0 ? (
          <>
            {/* Mobile list view with swipeable cards */}
            <div className="md:hidden bg-white rounded-xl overflow-hidden shadow-sm">
              {recommendedJobs.map((job) => (
                <SwipeableJobCard
                  key={job.id}
                  job={job}
                  onContact={() => handleJobContact(job)}
                  onSave={() => handleJobSave(job)}
                >
                  <JobCard job={job} isMobileLayout={true} />
                </SwipeableJobCard>
              ))}
            </div>
            
            {/* Desktop grid view */}
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-6">
              {recommendedJobs.map((job) => (
                <JobCard key={job.id} job={job} isMobileLayout={false} />
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-16 px-4 bg-white rounded-xl shadow-sm">
            <span className="material-symbols-outlined text-6xl text-slate-300 mb-4 block">
              search_off
            </span>
            <h3 className="font-heading font-semibold text-xl text-[#0A1929] mb-2">
              Ingen oppdrag funnet
            </h3>
            <p className="text-slate-500 mb-4">
              {selectedFylke 
                ? 'Det er ingen tilgjengelige oppdrag i dette fylket akkurat nå.'
                : 'Det er ingen tilgjengelige oppdrag akkurat nå.'
              }
            </p>
            {selectedFylke && (
              <button 
                onClick={() => setSelectedFylke(null)}
                className="bg-[#0A1929] text-white px-6 py-2 rounded-lg hover:bg-[#1E3A5F] transition-colors"
              >
                Se alle fylker
              </button>
            )}
          </div>
        )}

        {/* CTA Section */}
        {isAuthenticated && (
          <div className="mt-8 bg-gradient-to-r from-[#0A1929] to-[#1E3A5F] rounded-2xl p-6 md:p-8 text-white text-center">
            <h3 className="font-heading font-bold text-xl md:text-2xl mb-2">
              Trenger du hjelp med noe?
            </h3>
            <p className="text-white/70 mb-4">
              Legg ut et oppdrag og få hjelp fra lokale folk
            </p>
            <button
              onClick={() => navigate('/opprett-annonse')}
              className="bg-white text-[#0A1929] px-6 py-3 rounded-lg font-medium hover:bg-slate-100 transition-colors inline-flex items-center gap-2"
            >
              <span className="material-symbols-outlined">add</span>
              Opprett oppdrag
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;
